/**
 * Turns the Simple Bundles (Infinite Options) option dropdowns into image pickers.
 *
 * The app embed renders its <select>s into #simple-bundles-io-options inside the
 * product form and re-renders them whenever the variant changes. Those selects are
 * what actually submit properties[...] to /cart/add, so they stay in the DOM: this
 * script only builds an image picker next to each one and drives it, keeping the
 * app's own price/inventory behaviour intact.
 *
 * Option values are plain product titles, and a handle cannot be derived from a
 * title reliably, so images are looked up by title through
 * templates/search.swatch-map.liquid.
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '#simple-bundles-io-options';
  var CACHE_KEY = 'bundle-image-picker:swatch-map:v2';
  // Keeps the OR query well inside URL limits; batches are fetched in parallel.
  var TITLES_PER_REQUEST = 20;
  // Search matches loosely, so a batch can turn up far more than it asked for.
  // Paging stops as soon as every requested title is in, and never goes past
  // this — a bundle whose options read as common words must not walk the
  // catalogue looking for matches that were already found.
  var MAX_PAGES_PER_BATCH = 5;

  var config = window.bundleImagePickerConfig || {};
  var basePath = String(config.rootUrl || '/').replace(/\/$/, '');
  var soldOutLabel = config.soldOutLabel || 'Sold out';

  var swatchMap = null;
  var mapPromise = null;

  /* ------------------------------------------------------------------ *
   * title -> image url map
   * ------------------------------------------------------------------ */

  function has(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function writeCache(map) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(map));
    } catch (error) {
      /* private mode or quota: the map is simply refetched next page view */
    }
  }

  /**
   * The option values this product actually offers.
   *
   * Read from the app's own selects once they exist, and otherwise from the
   * identical markup the app leaves in window.SimpleBundles, which Liquid
   * prints inline. That inline script runs while the document is parsed and
   * this file is deferred, so the values are always there by the time we look
   * — the lookup never has to wait for the app to render.
   */
  function optionTitles() {
    var titles = [];
    var seen = {};

    function collect(root) {
      Array.prototype.forEach.call(root.querySelectorAll('option'), function (option) {
        var value = (option.value || '').trim();
        if (!value || has(seen, value)) return;
        seen[value] = true;
        titles.push(value);
      });
    }

    var container = document.querySelector(CONTAINER_SELECTOR);
    if (container) collect(container);

    var variants = (window.SimpleBundles && window.SimpleBundles.productVariants) || {};
    var id = document.querySelector('form[action*="/cart/add" i] [name="id"]');
    // Only the selected variant's options can be picked; fall back to every
    // variant when the form has not settled on one yet.
    var ids = id && variants[id.value] ? [id.value] : Object.keys(variants);

    ids.forEach(function (variantId) {
      var html = variants[variantId] && variants[variantId].bundle_options_selector_html;
      if (html) collect(new DOMParser().parseFromString(html, 'text/html'));
    });

    return titles;
  }

  function fetchBatch(titles) {
    var found = {};
    var query = titles
      .map(function (title) {
        return 'title:"' + title.replace(/"/g, ' ') + '"';
      })
      .join(' OR ');

    function fetchPage(page) {
      var url =
        basePath +
        '/search?type=product&view=swatch-map&page=' +
        page +
        '&q=' +
        encodeURIComponent(query);

      // No Accept: application/json — that makes Shopify treat a storefront page
      // URL as a JSON API route and answer 404. The view returns text/html.
      return fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (text) {
          return JSON.parse(text);
        })
        .then(function (data) {
          (data.items || []).forEach(function (item) {
            if (item && item.title && !has(found, item.title)) {
              found[item.title] = item.image || '';
            }
          });

          var outstanding = titles.some(function (title) {
            return !has(found, title);
          });
          if (outstanding && page < (data.pages || 1) && page < MAX_PAGES_PER_BATCH) {
            return fetchPage(page + 1);
          }
          return found;
        });
    }

    return fetchPage(1).catch(function () {
      return found;
    });
  }

  /**
   * The cache accumulates across every bundle visited this session, so the map
   * handed back is narrowed to the titles asked for. Anything wider and a
   * product whose own options all came back imageless would still look like it
   * had swatches, on the strength of some earlier product's.
   */
  function scopedTo(titles, known) {
    var map = {};
    titles.forEach(function (title) {
      if (has(known, title)) map[title] = known[title];
    });
    return map;
  }

  function loadSwatchMap() {
    if (mapPromise) return mapPromise;

    var titles = optionTitles();
    if (!titles.length) return Promise.resolve(null);

    var known = readCache();
    var missing = titles.filter(function (title) {
      return !has(known, title);
    });

    if (!missing.length) {
      swatchMap = scopedTo(titles, known);
      return Promise.resolve(swatchMap);
    }

    var batches = [];
    for (var i = 0; i < missing.length; i += TITLES_PER_REQUEST) {
      batches.push(missing.slice(i, i + TITLES_PER_REQUEST));
    }

    mapPromise = Promise.all(batches.map(fetchBatch)).then(function (results) {
      var merged = {};
      results.forEach(function (result) {
        Object.keys(result).forEach(function (title) {
          if (!has(merged, title)) merged[title] = result[title];
        });
      });

      // Search matches loosely, so keep only what was asked for — and remember
      // the titles that came back empty so they are not refetched all session.
      missing.forEach(function (title) {
        known[title] = has(merged, title) ? merged[title] : '';
      });

      writeCache(known);
      swatchMap = scopedTo(titles, known);
      // Cleared so a variant switch that introduces new options can look them
      // up; titles already in the cache resolve without another request.
      mapPromise = null;
      return swatchMap;
    });

    return mapPromise;
  }

  /* ------------------------------------------------------------------ *
   * picker
   * ------------------------------------------------------------------ */

  function labelFor(select) {
    var previous = select.previousElementSibling;
    if (previous && previous.tagName === 'LABEL') return previous;
    if (!select.id) return null;
    return document.querySelector('label[for="' + select.id.replace(/"/g, '\\"') + '"]');
  }

  function isSoldOut(option) {
    return option.disabled || option.getAttribute('data-inventory') === '0';
  }

  function buildChip(option) {
    var value = option.value;
    var imageUrl = (swatchMap && swatchMap[value]) || '';
    var soldOut = isSoldOut(option);

    var chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'bip-chip' + (soldOut ? ' bip-chip--sold-out' : '');
    chip.setAttribute('role', 'radio');
    chip.setAttribute('aria-checked', 'false');
    chip.dataset.value = value;
    chip.tabIndex = -1;
    if (soldOut) {
      chip.disabled = true;
      chip.setAttribute('aria-disabled', 'true');
    }

    var frame = document.createElement('span');
    frame.className = 'bip-chip__frame';

    if (imageUrl) {
      var image = document.createElement('img');
      image.className = 'bip-chip__image';
      image.src = imageUrl;
      image.alt = '';
      // Not lazy: browsers judge visibility poorly inside a horizontal scroller
      // and leave off-centre swatches blank.
      image.decoding = 'async';
      frame.appendChild(image);
    } else {
      frame.classList.add('bip-chip__frame--empty');
    }

    if (soldOut) {
      var badge = document.createElement('span');
      badge.className = 'bip-chip__badge';
      badge.textContent = soldOutLabel;
      frame.appendChild(badge);
    }

    var name = document.createElement('span');
    name.className = 'bip-chip__name';
    name.textContent = option.textContent.trim() || value;

    chip.appendChild(frame);
    chip.appendChild(name);
    return chip;
  }

  function buildPicker(select) {
    var options = Array.prototype.filter.call(select.options, function (option) {
      return option.value !== '';
    });
    if (!options.length) return false;

    var label = labelFor(select);
    var group = document.createElement('div');
    group.className = 'bip-group';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute(
      'aria-label',
      (label && label.textContent.trim()) || select.name || 'Options'
    );

    var track = document.createElement('div');
    track.className = 'bip-track';
    group.appendChild(track);

    var chips = options.map(function (option) {
      var chip = buildChip(option);
      track.appendChild(chip);
      return chip;
    });

    function selectable() {
      return chips.filter(function (chip) {
        return !chip.disabled;
      });
    }

    function sync() {
      var matched = null;
      chips.forEach(function (chip) {
        var isSelected = chip.dataset.value === select.value;
        if (isSelected) matched = chip;
        chip.classList.toggle('bip-chip--selected', isSelected);
        chip.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        chip.tabIndex = isSelected ? 0 : -1;
      });
      // Keep the group reachable by keyboard even when nothing is selected yet.
      if (!matched) {
        var first = selectable()[0];
        if (first) first.tabIndex = 0;
      }
      return matched;
    }

    function choose(chip, moveFocus) {
      if (!chip || chip.disabled) return;
      select.value = chip.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
      if (moveFocus) chip.focus();
    }

    track.addEventListener('click', function (event) {
      var chip = event.target.closest('.bip-chip');
      if (chip && track.contains(chip)) choose(chip);
    });

    track.addEventListener('keydown', function (event) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(event.key) === -1) return;

      var pool = selectable();
      if (!pool.length) return;

      var current = event.target.closest('.bip-chip');
      var index = pool.indexOf(current);
      var next;

      if (event.key === 'Home') next = pool[0];
      else if (event.key === 'End') next = pool[pool.length - 1];
      else {
        var step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        var from = index === -1 ? 0 : index;
        next = pool[(from + step + pool.length) % pool.length];
      }

      event.preventDefault();
      choose(next, true);
    });

    select.addEventListener('change', sync);

    // Replace an orphaned picker if the app re-rendered around this select.
    var sibling = select.nextElementSibling;
    if (sibling && sibling.classList.contains('bip-group')) sibling.remove();

    select.classList.add('bip-select-hidden');
    select.insertAdjacentElement('afterend', group);

    // Only reposition the track when the pre-selected chip is actually out of
    // sight, so a row that already fits is not left scrolled off its first chip.
    var selected = sync();
    if (selected) {
      // Measured against the track's own box rather than offsetLeft, which is
      // relative to whichever ancestor the theme happens to position.
      var trackBox = track.getBoundingClientRect();
      var chipBox = selected.getBoundingClientRect();
      if (chipBox.left < trackBox.left || chipBox.right > trackBox.right) {
        track.scrollLeft +=
          chipBox.left - trackBox.left - (track.clientWidth - chipBox.width) / 2;
      }
    }

    return true;
  }

  /* ------------------------------------------------------------------ *
   * wiring
   * ------------------------------------------------------------------ */

  function hasImages(map) {
    return Object.keys(map).some(function (title) {
      return map[title];
    });
  }

  function pendingSelects() {
    var container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return [];
    return Array.prototype.filter.call(container.querySelectorAll('select'), function (select) {
      return select.dataset.bipEnhanced !== '1';
    });
  }

  function reveal() {
    document.documentElement.classList.remove('bip-pending');
  }

  function run() {
    if (!pendingSelects().length) return;

    loadSwatchMap()
      .then(function (map) {
        // With no images to show, a dropdown beats a wall of text chips.
        if (!map || !hasImages(map)) return;

        pendingSelects().forEach(function (select) {
          if (!select.isConnected) return;
          if (buildPicker(select)) select.dataset.bipEnhanced = '1';
        });
      })
      .then(reveal, reveal);
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      run();
    });
  }

  // Nothing above may leave the app's own dropdowns hidden: on a product the
  // picker cannot serve — or if the lookup never returns — they are the only
  // way to configure the bundle.
  window.setTimeout(reveal, 3000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // The app injects and later re-renders its options asynchronously.
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
